import { useEffect, useMemo, useState } from "react";
import {
  USER_LIST_PAGE_SIZE,
  type UserListSortDir,
  type UserListSortKey,
  useGetUserList
} from "@dealer-center/ddc-pages-configuration-fetch";

/**
 * FR-013's **four** sort orders, as an `as const` union — never an `enum` (Principle III).
 *
 * Four options over three keys: "Name (A–Z)" and "Name (Z–A)" are the same key in two directions,
 * which is why the option id is modelled separately from the `{ sortKey, sortDir }` pair the wire
 * takes. Collapsing them into three options would drop a required order; expanding to six (every
 * key × both directions) would invent two FR-013 does not ask for.
 */
export const USER_LIST_SORT_OPTIONS = [
  "name-asc",
  "name-desc",
  "username",
  "email"
] as const;

export type UserListSortOption = (typeof USER_LIST_SORT_OPTIONS)[number];

/** The label key per option, resolved by the toolbar. Area-private copy (research R-013). */
export const USER_LIST_SORT_LABEL_KEYS: Record<UserListSortOption, string> = {
  "name-asc": "UM_SORT_NAME_ASC",
  "name-desc": "UM_SORT_NAME_DESC",
  username: "USERNAME",
  email: "EMAIL"
};

const SORT_PARAMS: Record<
  UserListSortOption,
  { sortKey: UserListSortKey; sortDir: UserListSortDir }
> = {
  "name-asc": { sortKey: "name", sortDir: "asc" },
  "name-desc": { sortKey: "name", sortDir: "desc" },
  username: { sortKey: "username", sortDir: "asc" },
  email: { sortKey: "email", sortDir: "asc" }
};

/**
 * How long the filter term settles before it reaches the query key.
 *
 * 300ms is the usual "as-you-type but not per-keystroke" figure. The exact number is not
 * load-bearing; **having** a debounce is (see the hook's comment).
 */
export const FILTER_DEBOUNCE_MS = 300;

export interface UseUserListQueryOptions {
  accountId?: string;
}

/**
 * Owns the user list's filter, sort and page state, and reads the matching server page.
 *
 * **The debounce is required, not an optimization.** FR-012a makes filtering server-side while
 * FR-012 keeps the prototype's as-you-type feel, so without it every keystroke is *both* a request
 * *and* a React Query cache entry — one entry per character of whatever the operator typed. The
 * term is debounced here, **before** it reaches `useGetUserList`, because that is where the query
 * key is built; debouncing inside the fetch hook would be too late.
 *
 * **Page resets when the filter or sort changes; selection does not.** A page-3 position is
 * meaningless against a freshly narrowed result set — it would show an empty page that reads as "no
 * users" (T044: no page is offered that does not exist). The **selection** is deliberately *not*
 * touched here: FR-017 requires it to survive a filter, sort or page change, discarding no pending
 * edits and prompting for nothing, **even when the change removes the selected user from the
 * visible rows**. That is why `selectedUserId` lives at the shell (`UserManager.tsx`) and not in
 * this hook — nothing here can reach it.
 */
export const useUserListQuery = ({ accountId }: UseUserListQueryOptions) => {
  /** What the operator has typed — drives the input, so it updates on every keystroke. */
  const [filterInput, setFilterInput] = useState("");
  /** What has settled — drives the query key, so it updates once per burst. */
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [sortOption, setSortOption] = useState<UserListSortOption>("name-asc");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filterInput);
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [filterInput]);

  const { sortKey, sortDir } = SORT_PARAMS[sortOption];

  const query = useGetUserList({
    accountId,
    filter: debouncedFilter,
    sortKey,
    sortDir,
    page,
    size: USER_LIST_PAGE_SIZE
  });

  /*
   * FR-011's discriminator, and FR-015's. `totalCount: 0` arrives identically for "this account has
   * no users" and "the filter matched nothing", so the payload cannot tell them apart — the UI can,
   * because it knows whether a term is applied. Read from the **settled** term, not the input, so
   * the message matches the rows currently on screen rather than what is mid-typing.
   */
  const isFiltered = Boolean(debouncedFilter.trim());

  const changeFilter = (term: string) => {
    setFilterInput(term);
    setPage(0);
  };

  const changeSort = (option: UserListSortOption) => {
    setSortOption(option);
    setPage(0);
  };

  return useMemo(
    () => ({
      query,
      /** Bind the input to this — it reflects every keystroke. */
      filterInput,
      /**
       * The **settled** term, for copy that quotes it back ("No users match “…”").
       *
       * Deliberately separate from `filterInput`: the message has to name the term the rows on
       * screen were actually filtered by. Quoting the live input would show the operator a term
       * mid-typing beside results for the previous one — the same reasoning that already builds
       * `isFiltered` from the settled value.
       */
      filterTerm: debouncedFilter.trim(),
      isFiltered,
      sortOption,
      page,
      changeFilter,
      changeSort,
      goToPage: setPage
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, filterInput, debouncedFilter, isFiltered, sortOption, page]
  );
};
